import React, { useId, useState } from "react";
import { previewOrderDiscount } from "../../services/local/orderDiscount";
import "../../styles/OrderBillActions.css";

const formatMoney = (amount) => new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(amount);
const paperPreferenceKey = "mozzo:customer-bill:paper-width";
const getPaper = () => {
  try { return ["55mm", "58mm", "80mm"].includes(localStorage.getItem(paperPreferenceKey)) ? localStorage.getItem(paperPreferenceKey) : "80mm"; }
  catch { return "80mm"; }
};

export default function OrderBillActions({ order, canDiscount, blockReason = "", onSaveDiscount, onPrint, onSettleZero }) {
  const id = useId();
  const [expanded, setExpanded] = useState(false);
  const [type, setType] = useState("fixed");
  const [value, setValue] = useState("");
  const [paper, setPaper] = useState(getPaper);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState(null);
  let preview;
  let previewError = "";
  if (expanded) {
    try { preview = previewOrderDiscount(order, { type, value }); }
    catch (error) { previewError = error.message; }
  }
  const run = async (key, operation, success) => {
    if (busy) return;
    setBusy(key);
    setMessage(null);
    try {
      await operation();
      if (key !== "print") setExpanded(false);
      setMessage({ error: false, text: success });
    } catch (error) { setMessage({ error: true, text: error.message || "No pudimos completar la accion." }); }
    finally { setBusy(""); }
  };
  const fullyDiscounted = Number(order.pricing?.finalTotal ?? order.totalAmount) === 0 && order.pricing?.manualDiscount;
  return <section className="order-bill-actions" aria-label="Cuenta del cliente">
    <div className="order-bill-toolbar">
      {canDiscount && <button type="button" disabled={Boolean(busy || blockReason)} title={blockReason || undefined}
        onClick={() => {
          setType(order.pricing?.manualDiscount?.type || "fixed");
          setValue(String(order.pricing?.manualDiscount?.value || ""));
          setMessage(null);
          setExpanded(!expanded);
        }}>{expanded ? "Ocultar descuento" : order.pricing?.manualDiscount ? "Modificar descuento" : "Aplicar descuento"}</button>}
      <label htmlFor={`${id}-paper`}>Papel del ticket
        <select id={`${id}-paper`} value={paper} disabled={Boolean(busy)} onChange={(event) => {
          setPaper(event.target.value);
          try { localStorage.setItem(paperPreferenceKey, event.target.value); } catch { /* Printing does not depend on storage. */ }
        }}><option value="55mm">55 mm</option><option value="58mm">58 mm</option><option value="80mm">80 mm</option></select>
      </label>
      <button type="button" className="order-bill-primary" disabled={Boolean(busy || expanded)}
        onClick={() => run("print", () => onPrint(paper), "Se abrio la impresion. Confirma la impresora y el ancho de papel en esa ventana.")}>
        {busy === "print" ? "Abriendo impresion..." : "Imprimir cuenta"}
      </button>
      {canDiscount && fullyDiscounted && !blockReason && <button type="button"
        disabled={Boolean(busy || (order.items || []).some((item) => !item.delivered))}
        onClick={() => run("settle", onSettleZero, "Cuenta bonificada confirmada, sin registrar un cobro. Ya puedes cerrar el pedido.")}>
        Confirmar cuenta bonificada
      </button>}
    </div>
    {canDiscount && blockReason && <p className="order-bill-help">{blockReason}</p>}
    {expanded && <div className="order-bill-editor">
      <p>Se aplica sobre el total despues de las promociones. Reemplaza cualquier descuento anterior del local.</p>
      <p className="order-bill-help">El descuento lo asume el restaurante. No reduce la base de la comision de Mozzo, que se calcula sobre el total anterior a este descuento.</p>
      <div className="order-bill-fields">
        <label htmlFor={`${id}-type`}>Tipo de descuento<select id={`${id}-type`} value={type} disabled={Boolean(busy)} onChange={(e) => setType(e.target.value)}>
          <option value="fixed">Pesos ($)</option><option value="percent">Porcentaje (%)</option>
        </select></label>
        <label htmlFor={`${id}-value`}>{type === "percent" ? "Porcentaje" : "Importe en pesos"}<input id={`${id}-value`}
          type="number" inputMode="decimal" min="0.01" max={type === "percent" ? 100 : undefined} step="0.01"
          value={value} disabled={Boolean(busy)} onChange={(e) => setValue(e.target.value)} /></label>
      </div>
      {preview && <div className="order-bill-preview"><span>Descuento: {formatMoney(preview.manualDiscount?.amount || 0)}</span>
        <strong>Nuevo total: {formatMoney(preview.finalTotal)}</strong>
        {preview.paymentPrices.cash.finalTotal !== preview.paymentPrices.other.finalTotal && <span>
          Efectivo: {formatMoney(preview.paymentPrices.cash.finalTotal)}. Otros medios: {formatMoney(preview.paymentPrices.other.finalTotal)}.
        </span>}
      </div>}
      {value !== "" && previewError && <p role="alert">{previewError}</p>}
      <div className="order-bill-toolbar">
        <button type="button" className="order-bill-primary" disabled={Boolean(busy || previewError || blockReason)}
          onClick={() => run("save", () => onSaveDiscount({ type, value: Number(value) }), "Descuento guardado en la cuenta.")}>
          {busy === "save" ? "Guardando descuento..." : "Guardar descuento"}</button>
        {order.pricing?.manualDiscount && <button type="button" disabled={Boolean(busy || blockReason)}
          onClick={() => run("remove", () => onSaveDiscount({ type: "none", value: 0 }), "Descuento del local quitado. Las promociones se conservan.")}>Quitar descuento</button>}
        <button type="button" disabled={Boolean(busy)} onClick={() => setExpanded(false)}>Cancelar</button>
      </div>
    </div>}
    {message && <p role={message.error ? "alert" : "status"} className={message.error ? "order-bill-error" : "order-bill-help"}>{message.text}</p>}
    <p className="order-bill-help">La cuenta impresa no acredita el pago ni reemplaza una factura. Configura el mismo ancho de papel en tu impresora.</p>
  </section>;
}
